import React, { useCallback, useEffect, useState } from "react";

import {
  confirmEdgeConnector,
  deleteEdgeConnector,
  listEdgeConnectors,
  previewEdgeConnectorLifecycle,
  restoreFR202TerminalTags,
  retryEdgeConnector,
} from "../../../api";
import { useNotify } from "../../../ToastContext";
import refreshIcon from "../../../assets/refresh.svg";
import chevronIcon from "../../../assets/chevron-right.svg";
import xmarkIcon from "../../../assets/xmark.svg";
import AddDeviceChooser from "./AddDeviceChooser";
import AddDeviceHistorianStep from "./AddDeviceHistorianStep";
import DeviceTemplatesModal, { SaveAsTemplateModal } from "./DeviceTemplatesModal";
import EdgeConnectorCreateForm from "./EdgeConnectorCreateForm";
import { edgeModbusSpecFromConnector, edgeModbusTemplateFromSpec } from "./edge-connector-code";
import { canEditConnector, edgeConnectorEditDraft, editConnectorKind, editConnectorReason } from "./edge-connector-draft";
import { applyHistorianToTagPaths } from "./tag-historian";
import {
  KIND_LABELS,
  RevisionReview,
  humanPointAddress,
  humanizeConnectorValue,
  preparePendingConnector,
  connectorValidationMessage,
} from "./EdgeConnectorReview";
import { FR202_PROFILE_ID } from "./platformEdgeRuntimes.hardware";
import { edgeHardwareProfileByID } from "./platformEdgeRuntimes.model";

const SYNC_LABELS = {
  in_sync: "Active",
  pending: "Applying…",
  rejected: "Failed",
  superseded: "Replaced",
  drifted: "Out of sync",
  rollback_pending: "Rolling back…",
  rolled_back: "Rolled back",
  unknown: "Waiting for device",
};

const syncPillClass = (state) => {
  if (state === "in_sync" || state === "rolled_back") return "isPositive";
  if (state === "rejected") return "isDanger";
  if (["pending", "drifted", "rollback_pending"].includes(state)) return "isWarning";
  return "isNeutral";
};

const ncdReceiverIsDiscovering = (connector) => (
  connector?.kind === "ncd-pr63-27c-ethernet"
  && String(connector?.applied?.actual_state || "").trim().toLowerCase() === "degraded"
  && /waiting for the NCD Ethernet receiver on the local network/i.test(String(connector?.applied?.error || ""))
);

const retainedGenericUSBConnection = (gateway, connector) => (
  gateway?.hardware_profile === "generic-linux-ipc-v1"
  && connector?.kind === "ncd-pr63-27c-usb"
);

const retainedGenericUSBStatusLine = (gateway, connector) => {
  if (!retainedGenericUSBConnection(gateway, connector)) return "";
  return connector?.desired_state === "running"
    ? "Move this receiver to Ethernet before changing it or turning it off. After it stops, it cannot be turned back on over USB on this device."
    : "This receiver cannot be turned back on over USB on this device. Manage sensors and move it to Ethernet to reconnect it.";
};

// Confirming normally applies the open review, but the sensor-receiver
// workflow passes its validated preview directly and skips the interstitial
// screen. Selecting between them cannot rely on "was an argument given": the
// same handler is wired to a button's onClick, which always supplies a
// MouseEvent. Only a candidate that actually carries a preview wins.
export const confirmTargetFrom = (candidate, openReview) =>
  (candidate?.preview ? candidate : openReview);

const lifecycleReview = (gateway, connector, preview, desiredState) => ({
  preview,
  action: desiredState === "running" ? "start" : "stop",
  ...(desiredState === "stopped" && retainedGenericUSBConnection(gateway, connector) ? {
    warning: {
      title: "Move this receiver to Ethernet first",
      message: "If you turn it off now, it cannot be turned back on over USB on this device. You can leave it stopped or reconnect it over Ethernet.",
    },
  } : {}),
});

// One pill per connection, and it must never contradict the status line:
// an in-sync connection reads as what it is doing (Running/Stopped) instead
// of the reconciliation verdict ("Active").
const connectorDisplayStatus = (connector) => {
  const state = connector?.sync_state;
  if (state === "in_sync") {
    if (connector?.desired_state === "running" && ["ncd-pr63-27c-usb", "ncd-pr63-27c-ethernet"].includes(connector?.kind)) {
      if (ncdReceiverIsDiscovering(connector)) return { label: "Finding receiver…", tone: "isNeutral" };
      const actualState = String(connector?.applied?.actual_state || "").trim().toLowerCase();
      if (actualState === "degraded") return { label: "Needs attention", tone: "isWarning" };
      if (actualState && actualState !== "running") return { label: "Disconnected", tone: "isDanger" };
      if (!actualState) return { label: "Waiting", tone: "isNeutral" };
    }
    return connector?.desired_state === "running"
      ? { label: "Running", tone: "isPositive" }
      : { label: "Stopped", tone: "isNeutral" };
  }
  return { label: SYNC_LABELS[state] || SYNC_LABELS.unknown, tone: syncPillClass(state) };
};

// A connection is presented as its tag, not as a separately named object.
// Multi-point connections (protocol servers) keep their stored name.
const connectorTitle = (connector) => {
  const mappings = Array.isArray(connector?.mappings) ? connector.mappings : [];
  if (mappings.length === 1) {
    const leaf = String(mappings[0].tag_path || "").split("/").filter(Boolean).pop();
    if (leaf) return leaf;
  }
  return connector?.name || "Connection";
};

// The device reports failures as a chain of wrapped Go errors. Only known,
// actionable categories may cross into customer copy; unknown diagnostics
// remain in support telemetry and receive one safe fallback here.
const DIAGNOSTIC_WRAPPER_PREFIXES = [
  /^activate connector revision:\s*/i,
  /^apply connector projection "[^"]*":\s*/i,
  /^apply GPIO connector inventory:\s*/i,
  /^apply modbus connector inventory:\s*/i,
  /^start projected Modbus inventory:\s*/i,
  /^apply MQTT connector inventory:\s*/i,
];

const DIAGNOSTIC_WRAPPER_SUFFIXES = [
  /\s*\(previous cross-protocol inventory restored\)$/i,
  /\s*\(previous inventory restored\)$/i,
  /;\s*restore previous [^;]*$/i,
];

const humanizeConnectorDiagnostic = (raw) => {
  let text = String(raw || "").trim();
  if (!text) return "";
  for (let pass = 0; pass < 8; pass++) {
    const before = text;
    for (const prefix of DIAGNOSTIC_WRAPPER_PREFIXES) text = text.replace(prefix, "");
    for (const suffix of DIAGNOSTIC_WRAPPER_SUFFIXES) text = text.replace(suffix, "");
    if (text === before) break;
  }
  let match = text.match(/^(.+?): output tag has no good value; fail-safe state applied$/i);
  if (match) {
    return "The output tag has no value yet, so the output stayed in its safe state. Write a value to the tag, then retry.";
  }
  if (/GPIO output readback did not match command/i.test(text)) {
    return "The pin's measured level didn't match the commanded value. Check wiring and pin conflicts, then retry.";
  }
  match = text.match(/GPIO BCM(\d+) is already managed by legacy binding "(.+?)"/i);
  if (match) {
    return `Pin BCM${match[1]} is already used by another mapping. Remove that mapping first.`;
  }
  if (/connector resource conflict|already claimed by connector/i.test(text)) {
    return "This receiver is already used by another connection. Edit or remove that connection first.";
  }
  if (/more than one NCD Ethernet receiver was found|multiple NCD Ethernet receivers/i.test(text)) {
    return "More than one Ethernet receiver was found. Edit this connection and choose a fixed receiver address.";
  }
  if (/sensor receiver could not be (reached|opened)/i.test(text)) {
    return "The receiver could not be reached. Check its power and connection, then try again.";
  }
  if (/sensor receiver disconnected|sensor receiver could not be read/i.test(text)) {
    return "The receiver disconnected. Check its power and connection; Control Seat will retry automatically.";
  }
  if (/validated sensor report could not be saved/i.test(text)) {
    return "A sensor report could not be saved. The receiver will retry automatically.";
  }
  if (/damaged sensor report|malformed sensor report/i.test(text)) {
    return "An incomplete or damaged sensor report was ignored. Control Seat is still listening for the next report.";
  }
  if (/unsupported sensor report|report from an unsupported sensor/i.test(text)) {
    return "A nearby sensor sent a report this connection does not support, so it was ignored.";
  }
  if (/sensor marked a report invalid/i.test(text)) {
    return "The sensor marked this reading invalid, so Control Seat is waiting for its next reading.";
  }
  if (/offline|not connected|unreachable/i.test(text)) {
    return "The device is offline or unreachable. Reconnect it, then retry.";
  }
  if (/timed out|timeout/i.test(text)) {
    return "The device did not respond in time. Check the connection, then retry.";
  }
  if (/tag.+(missing|not found|does not exist)/i.test(text)) {
    return "The connected tag no longer exists. Choose another tag or remove this connection.";
  }
  return "This connection needs attention. Check the device and connection settings, then retry.";
};

// One plain-English sentence describing where this connection stands,
// replacing the "Desired running · Device not reported · Desired revision N"
// plumbing readout. Revision detail lives in History.
//
// It must not restate the pill. The pill beside the title already says
// "Running", so a line beginning "Running · 1 point reporting." spends its
// first word telling the operator something they are already looking at,
// and the row reads as though the same fact were being reported twice.
// The line's job is the detail the pill cannot carry.
const connectorStatusLine = (connector, gatewayName) => {
  const actual = connector?.applied || {};
  const state = connector?.sync_state;
  const deviceName = gatewayName || "the device";
  if (state === "rejected") {
    return "The device refused this change and kept its previous setup.";
  }
  if (state === "pending" || state === "rollback_pending") {
    return `Waiting for ${deviceName} to confirm the change.`;
  }
  if (state === "drifted") {
    return `${deviceName} is running a different version of this connection. Retry to re-apply it.`;
  }
  if (state === "in_sync" || state === "rolled_back") {
    if (connector?.desired_state !== "running") return "Stopped on the device.";
    if (["ncd-pr63-27c-usb", "ncd-pr63-27c-ethernet"].includes(connector?.kind)) {
      if (ncdReceiverIsDiscovering(connector)) return "Looking for the Ethernet receiver on the local network…";
      if (actual.actual_state === "degraded") return "The receiver needs attention and will retry automatically.";
      if (actual.actual_state && actual.actual_state !== "running") return "The receiver is disconnected. Check its power and connection.";
      return "The receiver is connected and waiting for the next sensor report.";
    }
    if (connector?.kind === "modbus" && actual.device_state === "offline") {
      return "No read response from the field device. Commands can still be sent.";
    }
    if (connector?.kind === "modbus" && actual.device_state === "degraded") {
      return "Field-device communication is intermittent; the connection is retrying.";
    }
    const good = Number(actual.point_good || 0);
    const degraded = Number(actual.point_degraded || 0);
    if (degraded > 0) {
      return `${degraded} of ${good + degraded} point${good + degraded === 1 ? "" : "s"} waiting for data.`;
    }
    if (actual.actual_state === "degraded") return "The device reports a problem with this connection.";
    if (good > 0) return `${good} point${good === 1 ? "" : "s"} reporting.`;
    return "Waiting for the device to report point detail.";
  }
  return `${deviceName} hasn't reported this connection yet.`;
};

const CONNECTOR_CAPABILITY_LABELS = [
  ["gpio", "Built-in digital I/O"],
  ["modbus-tcp-client", "Modbus TCP"],
  ["modbus-tcp-server", "Modbus TCP server"],
  ["modbus-rtu-client", "Modbus RTU"],
  ["modbus-rtu-over-tcp-client", "Modbus RTU over TCP"],
  ["ncd-pr63-27c-usb-receiver-v1", "NCD machine health sensor"],
  ["ncd-pr63-27c-ethernet-receiver-v1", "NCD machine health sensor"],
  ["opcua-client", "OPC UA"],
  ["mqtt-direct-topic-v1", "MQTT direct topics"],
  ["enip-class1-scanner", "EtherNet/IP Class 1"],
  ["ethercat-master", "EtherCAT"],
  ["profinet-io-controller", "PROFINET"],
  ["fr202-analog-input-v1", "FR202 analog inputs"],
];

const connectorCapabilityLabels = (capabilities) => {
  const available = new Set(Array.isArray(capabilities) ? capabilities : []);
  return [...new Set(CONNECTOR_CAPABILITY_LABELS
    .filter(([capability]) => available.has(capability))
    .map(([, label]) => label))];
};

const connectorIsHumanProtocolService = (connector) => connector?.kind === "modbus_tcp_server";

const connectorOperationNeedsRuntimeReadiness = (desiredState) => desiredState === "running";

const connectorForLegacyGPIOUpgrade = (connectors, binding) => {
  const nativeAddress = `bcm:${binding?.bcm_pin}`;
  return (Array.isArray(connectors) ? connectors : []).find((connector) => connector.kind === "gpio" &&
    Array.isArray(connector.mappings) && connector.mappings.some((mapping) => mapping.native_address === nativeAddress));
};

export const FR202_GROUP_LABELS = { "Digital Inputs": "Digital inputs", "Digital Outputs": "Digital outputs" };

export const hasFR202BuiltinIO = (gateway, terminals) => (
  gateway?.hardware_profile === FR202_PROFILE_ID || terminals.length > 0
);

// This is a compact projection of the ordinary tag catalog, not a second I/O
// model. Paths follow user moves/renames and all mutations stay in Tags.
export const fr202GroupStatusLine = (rows) => {
  const missing = rows.filter((terminal) => terminal.tag_missing).length;
  if (missing === rows.length) return `${rows.length} terminals · ${missing} tags missing`;
  if (missing > 0) return `${rows.length} terminals · ${missing} ${missing === 1 ? "tag" : "tags"} missing`;
  const reporting = rows.filter((terminal) => terminal.quality === "good").length;
  if (reporting === rows.length) return `${rows.length} terminals · all reporting`;
  if (reporting === 0) return `${rows.length} terminals · none reporting yet`;
  return `${rows.length} terminals · ${reporting} reporting, ${rows.length - reporting} unavailable`;
};

export const fr202TerminalValueLabel = (terminal) => {
  if (terminal.tag_missing) return "Tag missing";
  if (terminal.quality !== "good" || !terminal.has_value) return "No value";
  return terminal.value ? "On" : "Off";
};

export const edgeConnectorConfigurationAvailable = (inventory) =>
  inventory?.configuration_available === true;

// Every terminal in a group lives in the same tag folder, so printing the full
// path on each row meant 16 copies of one string differing only in the last
// segment — the folder drowned the terminal. Return the shared folder so it can
// be stated once above the grid; "" when the rows have been moved apart, in
// which case each terminal keeps its own full path.
export const fr202GroupTagFolder = (rows) => {
  const folders = new Set((Array.isArray(rows) ? rows : []).map((terminal) => {
    const path = String(terminal.tag_path || "");
    const cut = path.lastIndexOf("/");
    return cut > 0 ? path.slice(0, cut) : "";
  }));
  if (folders.size !== 1) return "";
  const [only] = [...folders];
  return only;
};

function EdgeConnectorsModal({ gateway, canConfigure, initialKind = "", initialDraft = null, page = false, onClose, onChanged }) {
  const notify = useNotify();
  const [inventory, setInventory] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState("");
  const [confirmingRemove, setConfirmingRemove] = useState("");
  const [openBuiltinGroups, setOpenBuiltinGroups] = useState({});
  const [review, setReview] = useState(null);
  const [confirmStatus, setConfirmStatus] = useState("");
  const [creating, setCreating] = useState(Boolean(initialKind));
  const [draft, setDraft] = useState(initialDraft);
  const [createKind, setCreateKind] = useState(initialKind);
  // "" | "browse" | "pick" | "choose" — the same list, once to manage saved
  // devices and once to start a connection from one; "choose" is the BETA-53
  // "Agregar dispositivo" entry point that picks between "pick" and the
  // plain no-template form.
  const [templatesMode, setTemplatesMode] = useState("");
  const [savingTemplate, setSavingTemplate] = useState(null);
  // BETA-53: set right after a fresh connection (never an edit) is
  // confirmed, while `creating` stays true — the wizard's last screen asks
  // whether/how to record history for the tags that connection just used,
  // instead of a separate trip to Tags afterward.
  const [historianPrompt, setHistorianPrompt] = useState(null);
  const [historianBusy, setHistorianBusy] = useState(false);
  // In-flight + generation guard (same pattern as PLCProgramsModal): a slow
  // offline-edge response must not stack polls or overwrite fresher data.
  const loadStateRef = React.useRef({ promise: null, generation: 0 });

  const load = useCallback(() => {
    if (loadStateRef.current.promise) return loadStateRef.current.promise;
    const generation = ++loadStateRef.current.generation;
    const request = Promise.resolve()
      .then(() => listEdgeConnectors(gateway.id))
      .then((result) => {
        if (generation !== loadStateRef.current.generation) return;
        setInventory(result || { connectors: [], capabilities: [] });
        setError("");
      })
      .catch(() => {
        if (generation !== loadStateRef.current.generation) return;
        setError("I/O connections could not be loaded. Refresh the page and try again.");
      })
      .finally(() => {
        if (generation === loadStateRef.current.generation) {
          loadStateRef.current.promise = null;
          setLoading(false);
        }
      });
    loadStateRef.current.promise = request;
    return request;
  }, [gateway.id]);

  // A mutation must be followed by a read that started after that mutation.
  // Reusing an older polling request can briefly put the pre-change row back
  // on screen even though the device command was accepted. Wait out any poll,
  // then start one fresh request for the mutation result.
  const loadAfterMutation = useCallback(async () => {
    const current = loadStateRef.current.promise;
    if (current) await current;
    return load();
  }, [load]);

  useEffect(() => {
    const loadState = loadStateRef.current;
    setLoading(true);
    load();
    const timer = window.setInterval(load, 5000);
    return () => {
      window.clearInterval(timer);
      loadState.generation += 1;
      loadState.promise = null;
    };
  }, [load]);

  useEffect(() => {
    // Escape backs out of the open review first, then the creation form,
    // then closes the modal. While a confirm is in flight (mirrors the
    // disabled Cancel button below), Escape must not dismiss anything.
    // While the BETA-53 historian step is open, Escape is ignored outright —
    // the connection is already created, so there is nothing to "back out
    // of" short of an explicit Skip/Done.
    const onKey = (event) => {
      if (event.key !== "Escape" || busy === "confirm" || historianPrompt) return;
      if (review) setReview(null);
      else if (creating) setCreating(false);
      else onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose, review, busy, creating, historianPrompt]);

  const connectors = Array.isArray(inventory?.connectors) ? inventory.connectors : [];
  const legacyGPIOBindings = Array.isArray(inventory?.legacy_gpio_bindings) ? inventory.legacy_gpio_bindings : [];
  const capabilities = Array.isArray(inventory?.capabilities) ? inventory.capabilities : [];
  const serialPorts = Array.isArray(inventory?.serial_ports) ? inventory.serial_ports : [];
  const ncdUSBReceiverPorts = Array.isArray(inventory?.ncd_usb_receiver_ports) ? inventory.ncd_usb_receiver_ports : [];
  const fr202Terminals = Array.isArray(inventory?.fr202_terminals) ? inventory.fr202_terminals : [];
  const missingFR202Tags = fr202Terminals.filter((terminal) => terminal.tag_missing);
  // Terminal inventory is authoritative runtime evidence. The runtime list may
  // briefly be stale or partially hydrated while this page is refreshing, so
  // never hide discovered built-in I/O solely because its profile field is
  // missing from that separate response.
  const isFR202 = hasFR202BuiltinIO(gateway, fr202Terminals);
  const isFR201 = gateway.hardware_profile === "onlogic-fr201-v1";
  const supported = capabilities.includes("connector-config-v1") &&
    capabilities.includes("edge-tag-catalog-v1") &&
    capabilities.includes("edge-tag-identity-v1");
  // Fail closed until the control plane explicitly confirms it can complete
  // the reviewed connection. This prevents a mixed-version deployment from
  // creating an ordinary tag before discovering that device setup is down.
  const configurationAvailable = edgeConnectorConfigurationAvailable(inventory);
  const gpioBoard = inventory?.gpio_board;
  const gpioBoardLabel = gpioBoard?.status === "detected" && gpioBoard?.profile_label
    ? String(gpioBoard.profile_label)
    : "";

  const retry = async (connector) => {
    if (connectorIsHumanProtocolService(connector)) return;
    setBusy(`retry:${connector.connector_id}`);
    try {
      await retryEdgeConnector(gateway.id, connector.connector_id);
    } catch {
      notify({ type: "error", title: "Retry failed", message: "The connection could not be retried. Check the device connection and try again." });
      setBusy("");
      return;
    }
    notify({ type: "success", title: "Trying again", message: `${gateway.name} will re-apply ${connectorTitle(connector)}.` });
    try {
      await Promise.all([loadAfterMutation(), Promise.resolve(onChanged?.())]);
    } catch {
      notify({
        type: "warning",
        title: "Retry queued; status refresh delayed",
        message: "The retry was sent, but the device list could not be refreshed yet.",
      });
    } finally {
      setBusy("");
    }
  };

  const restoreBuiltinTags = async () => {
    setBusy("restore-fr202-tags");
    try {
      const result = await restoreFR202TerminalTags(gateway.id);
      const restored = Number(result?.restored || 0);
      const missing = Number(result?.missing || 0);
      if (Array.isArray(result?.fr202_terminals)) {
        setInventory((current) => ({ ...(current || {}), fr202_terminals: result.fr202_terminals }));
      }
      if (missing > 0) {
        notify({
          type: "warning",
          title: "Some tags still need attention",
          message: "Follow the guidance on the affected terminal, then restore the tags again.",
        });
      } else {
        notify({
          type: "success",
          title: "Built-in I/O tags restored",
          message: restored > 0
            ? `${restored} ${restored === 1 ? "tag is" : "tags are"} available again.`
            : "All built-in I/O tags are already available.",
        });
      }
    } catch {
      notify({
        type: "error",
        title: "Tags could not be restored",
        message: "Reconnect the runtime, then try Restore tags again.",
      });
      setBusy("");
      return;
    }
    try {
      await Promise.resolve(onChanged?.());
    } catch {
      notify({
        type: "warning",
        title: "Tags restored; status refresh delayed",
        message: "The tags are available, but the runtime list could not be refreshed yet.",
      });
    } finally {
      setBusy("");
    }
  };

  const previewLifecycle = async (connector) => {
    if (connectorIsHumanProtocolService(connector)) return;
    const desiredState = connector.desired_state === "running" ? "stopped" : "running";
    setBusy(`lifecycle:${connector.connector_id}`);
    try {
      const preview = await previewEdgeConnectorLifecycle(gateway.id, connector.connector_id, desiredState);
      setReview(lifecycleReview(gateway, connector, preview, desiredState));
    } catch {
      notify({ type: "error", title: "Change could not be prepared", message: "Refresh the connection list and try again." });
    } finally {
      setBusy("");
    }
  };

  const edit = (connector) => {
    const nextKind = editConnectorKind(connector);
    const nextDraft = edgeConnectorEditDraft(connector);
    if (!nextKind || !nextDraft) {
      notify({
        type: "error",
        title: "Connection cannot be edited",
        message: "Remove this connection and connect it again to change its settings.",
      });
      return;
    }
    setReview(null);
    setConfirmingRemove("");
    setDraft(nextDraft);
    setCreateKind(nextKind);
    setCreating(true);
  };

  const confirm = async (candidate) => {
    const target = confirmTargetFrom(candidate, review);
    if (!target?.preview) return;
    if (!configurationAvailable) {
      notify({
        type: "error",
        title: "I/O setup is temporarily unavailable",
        message: "Contact Control Seat support. No tags or device settings have been changed.",
      });
      return;
    }
    setBusy("confirm");
    setConfirmStatus("Applying…");
    let confirmedPreview = target.preview;
    try {
      if (target.pendingTag || target.pendingTags?.length) {
        confirmedPreview = await preparePendingConnector({
          gateway,
          pendingTag: target.pendingTag,
          pendingTags: target.pendingTags,
          spec: target.spec,
          expectedRevision: target.expectedRevision,
          onStatus: setConfirmStatus,
          onCreated: (fullPath) => setReview((current) => {
            if (current?.pendingTag?.fullPath === fullPath) {
              return { ...current, pendingTag: { ...current.pendingTag, created: true } };
            }
            if (Array.isArray(current?.pendingTags)) {
              return {
                ...current,
                pendingTags: current.pendingTags.map((tag) => tag.fullPath === fullPath ? { ...tag, created: true } : tag),
              };
            }
            return current;
          }),
        });
      }
      setConfirmStatus(`Sending to ${gateway.name}…`);
      await confirmEdgeConnector(gateway.id, confirmedPreview);
    } catch (err) {
      const raw = String(err?.message || "").toLowerCase();
      const staleReview = /changed since|out of date|revision conflict|refresh .*try again/.test(raw);
      const message = err?.publicMessage || connectorValidationMessage(err) || (staleReview
        ? "This connection changed after you reviewed it. Refresh the page and try Connect again."
        : /already exists/.test(raw)
          ? "A tag with this name already exists. Cancel and choose it under Existing tag, or use another name."
          : /offline|not online|unreachable|not connected/.test(raw)
            ? `${gateway.name} is offline. Reconnect it, then try Connect again.`
            : "The connection could not be completed. Check the device settings and try Connect again.");
      notify({ type: "error", title: "Connection not completed", message });
      setConfirmStatus("");
      setBusy("");
      return;
    }
    notify({
      type: "success",
      title: "Sent to device",
      message: `${gateway.name} is applying the change; the connection shows Running once it confirms.`,
    });

    // BETA-53: a fresh connection (never an edit) gets one more screen —
    // whether/how to record history for the tags it just used — instead of
    // silently finishing here and leaving that for a separate trip to Tags.
    // `confirmedPreview.mappings` is the exact, final set of points that was
    // just confirmed (the same field EdgeConnectorReview renders), so this
    // never depends on the tag-search index having caught up.
    const tagPaths = target.action === "create"
      ? [...new Set((Array.isArray(confirmedPreview?.mappings) ? confirmedPreview.mappings : [])
          .map((mapping) => String(mapping?.tag_path || "").trim())
          .filter(Boolean))]
      : [];
    if (tagPaths.length > 0) {
      setHistorianPrompt({ tagPaths });
      setConfirmStatus("");
      setBusy("");
      return;
    }

    setReview(null);
    setCreating(false);
    setDraft(null);
    try {
      await Promise.all([loadAfterMutation(), Promise.resolve(onChanged?.())]);
    } catch {
      notify({
        type: "warning",
        title: "Change sent; status refresh delayed",
        message: "The change was sent, but the device list could not be refreshed yet.",
      });
    } finally {
      setConfirmStatus("");
      setBusy("");
    }
  };

  // BETA-53 historian step: `policy` is null when the operator chose Skip
  // (or unchecked "Enable history"), otherwise the {enabled, historian_mode,
  // historian_interval_ms?} the Tags panel's per-tag toggle already sends.
  // Chunked at 64 so a large multi-point device never turns a working
  // connection into a partial failure the way set_tags_historian's own
  // 64-tag cap does for the agent.
  const finishHistorianStep = async (policy) => {
    const tagPaths = historianPrompt?.tagPaths || [];
    if (policy && tagPaths.length > 0) {
      setHistorianBusy(true);
      const results = [];
      for (let start = 0; start < tagPaths.length; start += 64) {
        // eslint-disable-next-line no-await-in-loop
        results.push(...(await applyHistorianToTagPaths(tagPaths.slice(start, start + 64), policy)));
      }
      const failed = results.filter((r) => !r.ok);
      if (failed.length > 0) {
        notify({
          type: "warning",
          title: "History could not be enabled on every tag",
          message: `Enabled on ${results.length - failed.length} of ${results.length} tag(s); check ${failed.map((r) => `"${r.path}"`).join(", ")} under Tags.`,
        });
      } else {
        notify({ type: "success", title: "History enabled", message: `History is on for ${results.length} tag(s).` });
      }
      setHistorianBusy(false);
    }
    setHistorianPrompt(null);
    setReview(null);
    setCreating(false);
    setDraft(null);
    try {
      await Promise.all([loadAfterMutation(), Promise.resolve(onChanged?.())]);
    } catch {
      notify({
        type: "warning",
        title: "Change sent; status refresh delayed",
        message: "The change was sent, but the device list could not be refreshed yet.",
      });
    }
  };

  // Removing a connection is one action — Always Works contract
  // (docs/always-works-contract.md). The backend queues a signed stop to the
  // device when needed; a still-running device instance stays visible in the
  // list until that stop lands, so nothing is silently forgotten.
  const remove = async (connector) => {
    setBusy(`remove:${connector.connector_id}`);
    try {
      await deleteEdgeConnector(gateway.id, connector.connector_id, connector.desired_revision);
    } catch (error) {
	  const detail = String(error?.publicMessage || error?.message || "");
      const stale = /changed since|out of date|refresh .*try again/i.test(detail);
      notify({
        type: "error",
        title: "Remove failed",
        message: stale
          ? "This connection changed after the page loaded. Refresh it, then review Remove again."
          : "The connection could not be removed. Check the device connection and try again.",
      });
      setBusy("");
      return;
    }
    setConfirmingRemove("");
    notify({
      type: "success",
      title: "Connection removed",
      message: `${connectorTitle(connector)} is no longer connected to ${gateway.name}.`,
    });
    try {
      await Promise.all([loadAfterMutation(), Promise.resolve(onChanged?.())]);
    } finally {
      setBusy("");
    }
  };

  // A template fills the wizard; it never connects anything on its own. The
  // spec the server derived becomes the draft, and the operator lands on the
  // ordinary form with every point already in place.
  const useTemplate = ({ draft: templateDraft, kind }) => {
    setTemplatesMode("");
    setReview(null);
    setConfirmingRemove("");
    setDraft(templateDraft);
    setCreateKind(kind);
    setCreating(true);
  };

  const migrateLegacyGPIO = (binding) => {
    const existing = connectorForLegacyGPIOUpgrade(connectors, binding);
    setDraft({
      connector_id: existing?.connector_id || "",
      legacy_binding_id: binding.id,
      name: existing?.name || binding.tag_name || `GPIO${binding.bcm_pin}`,
      tag_path: binding.tag_path,
      direction: binding.direction,
      bcm_pin: binding.bcm_pin,
      bias: binding.bias,
      debounce_ms: binding.debounce_ms,
      active_low: binding.active_low,
      safe_state: binding.safe_state,
    });
    setReview(null);
    setCreating(true);
  };

  return (
	<div className={page ? "gwConnectionsPage" : "gwOverlay"} onClick={!page && !review ? onClose : undefined} style={page ? undefined : { zIndex: 110 }}>
      <div className={page ? "gwConnectionsPageInner" : "gwModal gwEdgeProgramsModal"} role={page ? undefined : "dialog"} aria-modal={page ? undefined : "true"} aria-labelledby="edge-connectors-title" onClick={page ? undefined : (event) => event.stopPropagation()}>
        <div className={page ? "gwPageHeader gwConnectionsPageHeader" : "gwModalHeader"}>
          <div>
            {page ? (
              creating ? (
                // Back unwinds one level: leave the connection form for the
                // I/O list, not the whole page. During review or an in-flight
                // confirm it locks, like the modal close — the change resolves
                // through its own outcome, never a stray back click.
				<button type="button" className="gwGhostBtn plcRuntimeDetailBack" disabled={Boolean(review) || Boolean(historianPrompt) || busy === "confirm"} onClick={() => { setCreating(false); setDraft(null); }}>
                  <img src={chevronIcon} alt="" className="gwIcon" />
                  I/O & connections
                </button>
              ) : (
				<button type="button" className="gwGhostBtn plcRuntimeDetailBack" disabled={Boolean(review) || Boolean(historianPrompt) || busy === "confirm"} onClick={onClose}>
                  <img src={chevronIcon} alt="" className="gwIcon" />
                  {gateway.name}
                </button>
              )
            ) : null}
            <div className={page ? "gwPageTitle" : "gwModalTitle"} id="edge-connectors-title">I/O & connections</div>
            <div className={page ? "gwPageSubtitle" : "gwModalSubtitle"}>Connect sensors, equipment, tags, and local I/O on {gateway.name}</div>
          </div>
          {page ? (
            <div className="gwHeaderActions">
              <button type="button" className="gwIconButton" disabled={loading || Boolean(review) || Boolean(historianPrompt)} onClick={() => { setLoading(true); load(); }} aria-label="Refresh I/O connections" title="Refresh">
                <img src={refreshIcon} alt="" className="gwIcon" />
              </button>
              {!creating && !review ? (
                <>
                  <button type="button" className="gwSecondaryBtn" disabled={loading || Boolean(busy)} onClick={() => setTemplatesMode("browse")}>Device templates</button>
                  <button type="button" className="gwPrimaryBtn" disabled={loading || !supported || !configurationAvailable || !canConfigure || Boolean(busy)} onClick={() => setTemplatesMode("choose")}>Add device</button>
                </>
              ) : null}
            </div>
          ) : (
			<button type="button" className="gwModalClose" onClick={onClose} disabled={Boolean(review) || Boolean(historianPrompt) || busy === "confirm"} aria-label="Close">
              <img src={xmarkIcon} alt="" className="gwIcon" />
            </button>
          )}
        </div>
        <div className={page ? "gwConnectionsPageBody" : "gwModalBody gwEdgeProgramsBody"}>
          {!supported && !loading ? (
            <div className="gwNotice isSoft">
              This device needs a runtime update before tags can be connected here.
            </div>
          ) : null}
          {supported && !loading && !configurationAvailable ? (
            <div className="gwAlert" role="alert">
              <div className="gwAlertTitle">I/O setup is temporarily unavailable</div>
              <div className="gwAlertBody">Contact Control Seat support. No tags or device settings have been changed.</div>
            </div>
          ) : null}
          {supported && !loading ? (
            <div className="gwEdgeCapabilityLines" aria-label="Device connection status">
              <span><strong>{gateway.status === "online" ? "Online" : "Offline"}</strong></span>
              {capabilities.includes("gpio") && gpioBoardLabel ? <span>{gpioBoardLabel}</span> : null}
              {capabilities.includes("gpio") && !gpioBoardLabel ? (
                <span>GPIO is unavailable until the board is recognized.</span>
              ) : null}
            </div>
          ) : null}
          {!page && supported && configurationAvailable && !loading && !creating && !review ? (
            <div className="gwHeaderActions">
              <button type="button" className="gwSecondaryBtn" disabled={Boolean(busy)} onClick={() => setTemplatesMode("browse")}>
                Device templates
              </button>
              <button
                type="button"
                className="gwPrimaryBtn"
                disabled={!canConfigure || Boolean(busy)}
                onClick={() => setTemplatesMode("choose")}
              >
                Add device…
              </button>
            </div>
          ) : null}
          {error ? (
            <div className="gwAlert" role="alert">
              <div className="gwAlertTitle">Connector inventory unavailable</div>
              <div className="gwAlertBody">{error}</div>
            </div>
          ) : null}
          {creating && configurationAvailable && historianPrompt ? (
            <AddDeviceHistorianStep
              tagCount={historianPrompt.tagPaths.length}
              busy={historianBusy}
              onSkip={() => finishHistorianStep(null)}
              onApply={(policy) => finishHistorianStep(policy)}
            />
          ) : null}
          {creating && configurationAvailable && !historianPrompt ? (
            <div style={{ display: review ? "none" : "" }}>
              {/* Connecting can take several seconds when it also has to create
                  and deploy a tag first. Without the review screen this is the
                  only place those steps are named. */}
              {busy === "confirm" && confirmStatus ? (
                <div className="gwHint" role="status">{confirmStatus}</div>
              ) : null}
              <EdgeConnectorCreateForm
                gateway={gateway}
                capabilities={capabilities}
                gpioBoard={gpioBoard}
                serialPorts={serialPorts}
                ncdUSBReceiverPorts={ncdUSBReceiverPorts}
                existingConnectorNames={connectors.map((connector) => connector.name)}
                initialKind={draft?.legacy_binding_id ? "gpio" : createKind}
                initialDraft={draft}
                busy={Boolean(busy)}
                notify={notify}
                onCancel={() => { setCreating(false); setDraft(null); }}
                onPreview={(preview, pendingTagOrTags, expectedRevision) => {
                  const pendingTags = Array.isArray(pendingTagOrTags)
                    ? pendingTagOrTags
                    : pendingTagOrTags ? [pendingTagOrTags] : [];
                  const next = {
                    preview,
                    action: draft?.connector_id ? "edit" : "create",
                    ...(!Array.isArray(pendingTagOrTags) && pendingTags.length ? { pendingTag: pendingTags[0], spec: preview } : {}),
                    ...(Array.isArray(pendingTagOrTags) && pendingTags.length ? { pendingTags, spec: preview } : {}),
                    ...(Number.isSafeInteger(expectedRevision) ? { expectedRevision } : {}),
                  };
                  // Building a connection is one step: the form already states
                  // every setting, so its review only restated them. The
                  // non-mutating preview call stays as the validation gate and
                  // reports its failure inline, on the form, where it can be
                  // fixed. Lifecycle and removal keep their own confirmations.
                  confirm(next);
                }}
              />
            </div>
          ) : null}
          {loading && !inventory ? <div className="gwHint">Reading connector inventory…</div> : null}
          {!creating && !error && isFR201 ? (
            <div className="gwCardSection">
              <div className="gwSectionTitle">Built-in serial port</div>
              <div className="gwCardSectionBody">
                <div className="gwEdgeProgramRow">
                  <div className="gwEdgeProgramMain">
                    <div className="gwEdgeRuntimeTitleRow">
                      <span className="gwListTitle">RS-232 / RS-422 / RS-485</span>
                      <span className={`gwStatusPill ${serialPorts.length ? "isPositive" : "isWarning"}`}>
                        {serialPorts.length ? "Detected" : "Not detected"}
                      </span>
                    </div>
                    <div className="gwListMeta">
                      Set the electrical mode required by the field device, then follow its wiring diagram.
                    </div>
                  </div>
                  <button
                    type="button"
                    className="gwSecondaryBtn"
                    disabled={!canConfigure || !supported || !serialPorts.length || Boolean(busy)}
                    onClick={() => { setDraft(null); setCreateKind("modbus-rtu"); setCreating(true); }}
                  >
                    Set up Modbus RTU
                  </button>
                </div>
              </div>
            </div>
          ) : null}
          {!loading && !error && !creating && connectors.length === 0 && !isFR202 ? (
            <div className="gwEmptyState gwEmptyCompact">
              <div className="gwEmptyTitle">No device connections yet</div>
              <div className="gwEmptyBody">Connect a sensor or field device to {gateway.name}. Tag-based protocols can use an existing tag or create one during setup.</div>
            </div>
          ) : null}
          {!creating && isFR202 ? (
            <div className="gwCardSection">
              <div className="gwSectionTitleRow gwSectionTitle">
                <span>Built-in I/O on {gateway.name}</span>
                <span className="gwSectionBadge">{edgeHardwareProfileByID(FR202_PROFILE_ID)?.label || "FR202"}</span>
              </div>
              <div className="gwCardSectionBody">
                {missingFR202Tags.length > 0 ? (
                  <div className="gwNotice isSoft gwFR202TagRecovery" role="status">
                    <div>
                      {missingFR202Tags.length} built-in I/O {missingFR202Tags.length === 1 ? "tag is" : "tags are"} missing.
                      Restore {missingFR202Tags.length === 1 ? "it" : "them"} without changing the other tags.
                    </div>
                    <button
                      type="button"
                      className="gwSecondaryBtn"
                      disabled={!canConfigure || Boolean(busy)}
                      onClick={restoreBuiltinTags}
                    >
                      {busy === "restore-fr202-tags" ? "Restoring…" : "Restore tags"}
                    </button>
                  </div>
                ) : null}
                {fr202Terminals.length === 0 ? (
                  <div className="gwHint">Waiting for the FR202 terminal tags to report.</div>
                ) : (
                  ["Digital Inputs", "Digital Outputs"].map((group) => {
                    const rows = fr202Terminals.filter((terminal) => terminal.group === group);
                    if (rows.length === 0) return null;
                    const open = Boolean(openBuiltinGroups[group]);
                    return (
                      <div className="gwEdgeProgramRow" key={group}>
                        <div className="gwEdgeProgramMain">
                          <div className="gwEdgeRuntimeTitleRow">
                            <span className="gwListTitle">{FR202_GROUP_LABELS[group]}</span>
                            <span className="gwStatusPill isNeutral">Hardware</span>
                          </div>
                          <div className="gwListMeta" role="status">{fr202GroupStatusLine(rows)}</div>
                          {open ? (() => {
                            const folder = fr202GroupTagFolder(rows);
                            return (
                              <div className="gwFR202Terminals">
                                {folder ? <div className="gwListMeta gwFR202TerminalsFolder">Tags in {folder}</div> : null}
                                <div className="gwFR202TerminalGrid">
                                  {rows.map((terminal) => {
                                    const unavailable = terminal.quality !== "good";
                                    return (
                                      <div
                                        className={`gwFR202Terminal${unavailable ? " isUnavailable" : ""}`}
                                        key={terminal.terminal}
                                        title={terminal.tag_path || ""}
                                      >
                                        <span className="gwFR202TerminalName">{terminal.terminal}</span>
                                        <span className="gwFR202TerminalValue">{fr202TerminalValueLabel(terminal)}</span>
                                        {folder ? null : <span className="gwFR202TerminalPath">{terminal.tag_path}</span>}
                                        {unavailable && terminal.diagnostic ? (
                                          <span className="gwFR202TerminalPath">{humanizeConnectorDiagnostic(terminal.diagnostic)}</span>
                                        ) : null}
                                      </div>
                                    );
                                  })}
                                </div>
                              </div>
                            );
                          })() : null}
                        </div>
                        <div className="gwRowActions">
                          <button
                            type="button"
                            className="gwRowAction"
                            onClick={() => setOpenBuiltinGroups((previous) => ({ ...previous, [group]: !open }))}
                          >
                            {open ? "Hide terminals" : `Show ${rows.length} terminals`}
                          </button>
                        </div>
                      </div>
                    );
                  })
                )}
              </div>
            </div>
          ) : null}
          {!creating && legacyGPIOBindings.length > 0 ? (
            <div className="gwCardSection gwLegacyGPIOSection">
              <div className="gwSectionTitleRow gwSectionTitle">
                <span>Existing GPIO mappings</span>
                <span className="gwSectionBadge">Upgrade available</span>
              </div>
              <div className="gwCardSectionBody">
                {legacyGPIOBindings.map((binding) => (
                  <div className="gwEdgeProgramRow" key={binding.id}>
                    <div className="gwEdgeProgramMain">
                      <div className="gwEdgeRuntimeTitleRow">
                        <span className="gwListTitle">{binding.tag_name}</span>
                        <span className="gwStatusPill isNeutral">Legacy</span>
                      </div>
                      <div className="gwListMeta">GPIO{binding.bcm_pin} · {binding.direction === "output" ? "Output" : "Input"} · {binding.tag_path}</div>
                    </div>
                    <div className="gwRowActions">
                      <button type="button" className="gwRowAction" disabled={!canConfigure || Boolean(busy) || Boolean(review)} onClick={() => migrateLegacyGPIO(binding)}>Upgrade mapping</button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ) : null}
          {(creating ? [] : connectors).map((connector) => {
            const actual = connector.applied || {};
            const pending = ["pending", "rollback_pending"].includes(connector.sync_state);
            const status = connectorDisplayStatus(connector);
            const humanProtocolService = connectorIsHumanProtocolService(connector);
            const mappings = Array.isArray(connector.mappings) ? connector.mappings : [];
            const tagDeleted = mappings.some((mapping) => mapping.tag_missing);
            const cannotEditReason = !humanProtocolService && !tagDeleted && !canEditConnector(connector)
              ? editConnectorReason(connector)
              : "";
            const stopped = connector.sync_state === "in_sync" && connector.desired_state !== "running";
            const retainedUSBStatus = retainedGenericUSBStatusLine(gateway, connector);
            const canRetryDesired = !connectorOperationNeedsRuntimeReadiness(connector.desired_state) || supported;
            return (
              <div className="gwEdgeProgramRow" key={connector.connector_id}>
                <div className="gwEdgeProgramMain">
                  <div className="gwEdgeRuntimeTitleRow">
                    <span className="gwListTitle">{connectorTitle(connector)}</span>
                    <span
                      className={`gwStatusPill ${status.tone}`}
                      role="status"
                      aria-live="polite"
                    >
                      {status.label}
                    </span>
                  </div>
                  {mappings.length ? mappings.map((mapping) => (
                    <div className="gwListMeta" key={`${mapping.tag_path}:${mapping.native_address}`}>
                      {mapping.tag_path} ↔ {humanPointAddress(mapping.native_address)} · {humanizeConnectorValue(connector.kind, KIND_LABELS)}
                    </div>
                  )) : <div className="gwListMeta">{humanizeConnectorValue(connector.kind, KIND_LABELS)}</div>}
                  {tagDeleted ? (
                    <div className="gwListMeta" role="status">
                      This tag no longer exists. Remove the connection, or make a new one for another tag.
                    </div>
                  ) : (
                    <div className="gwListMeta" role="status">{connectorStatusLine(connector, gateway.name)}</div>
                  )}
                  {connector.diagnostic ? (
                    <div className="gwListMeta" role="status">
                      {humanizeConnectorDiagnostic(connector.diagnostic)}
                    </div>
                  ) : null}
                  {actual.error && humanizeConnectorDiagnostic(actual.error) !== humanizeConnectorDiagnostic(connector.diagnostic || "") ? (
                    <div className="gwListMeta" role="status">
                      {humanizeConnectorDiagnostic(actual.error)}
                    </div>
                  ) : null}
                  {retainedUSBStatus ? <div className="gwListMeta" role="status">{retainedUSBStatus}</div> : null}
                  {cannotEditReason ? <div className="gwListMeta">{cannotEditReason}</div> : null}
                </div>
                <div className="gwRowActions">
                  {confirmingRemove === connector.connector_id ? (
                    <>
                      <span className="gwListMeta">Remove this connection?</span>
					  <button type="button" className="gwGhostBtn" disabled={Boolean(busy)} onClick={() => setConfirmingRemove("")}>Cancel</button>
					  <button type="button" className="gwDangerBtn" disabled={!canConfigure || Boolean(busy)} onClick={() => remove(connector)}>
                        {busy === `remove:${connector.connector_id}` ? "Removing…" : "Remove"}
                      </button>
                    </>
                  ) : (
                    <>
                      {!humanProtocolService && !tagDeleted && (connector.sync_state === "rejected" || connector.sync_state === "drifted" || pending) ? (
						<button type="button" className="gwRowAction" disabled={!canConfigure || !canRetryDesired || Boolean(busy) || Boolean(review)} onClick={() => retry(connector)}>
                          {busy === `retry:${connector.connector_id}` ? "Trying…" : "Try again"}
                        </button>
                      ) : null}
                      {!humanProtocolService && !tagDeleted ? (
                        <button
                          type="button"
                          className="gwRowAction"
						  disabled={!canConfigure || (stopped && (!supported || retainedGenericUSBConnection(gateway, connector))) || Boolean(busy) || Boolean(review)}
                          onClick={() => previewLifecycle(connector)}
                        >
                          {busy === `lifecycle:${connector.connector_id}` ? "Preparing…" : stopped ? "Turn on" : "Turn off"}
                        </button>
                      ) : null}
                      {!humanProtocolService && !tagDeleted && canEditConnector(connector) ? (
                        <button
                          type="button"
                          className="gwRowAction"
						  disabled={!canConfigure || Boolean(busy) || Boolean(review)}
                          onClick={() => edit(connector)}
                        >
                          {["ncd-pr63-27c-usb", "ncd-pr63-27c-ethernet"].includes(connector.kind) ? "Manage sensors" : "Edit"}
                        </button>
                      ) : null}
                      {/* Only a Modbus client whose settings the server can
                          read back in full can be saved: a template built from
                          a partial reading would be a register map with holes
                          in it. */}
                      {edgeModbusSpecFromConnector(connector) ? (
                        <button
                          type="button"
                          className="gwRowAction"
                          disabled={!canConfigure || Boolean(busy) || Boolean(review)}
                          onClick={() => setSavingTemplate(connector)}
                        >
                          Save as template
                        </button>
                      ) : null}
                      {humanProtocolService ? <span className="gwListMeta">Manage in Servers</span> : null}
                      {/* No trailing ellipsis on an idle action.
                          Everywhere else in this UI "…" means work is in
                          flight — "Preparing…", "Removing…", "Rolling
                          back…", "Loading hardware…" — so an idle button
                          wearing one reads as already running, and every
                          row showed two of them. The busy labels below and
                          on the lifecycle button carry the ellipsis, which
                          is where it belongs. */}
					  <button type="button" className="gwRowAction" disabled={!canConfigure || Boolean(busy) || Boolean(review)} onClick={() => setConfirmingRemove(connector.connector_id)}>
                        Remove
                      </button>
                    </>
                  )}
                </div>
              </div>
            );
          })}
          <RevisionReview
            review={review}
            gatewayName={gateway.name}
            busy={busy === "confirm"}
            busyLabel={confirmStatus}
            canApply={canConfigure && (!connectorOperationNeedsRuntimeReadiness(review?.preview?.desired_state) || supported)}
            onCancel={() => setReview(null)}
            onConfirm={confirm}
          />
          {!creating && connectors.some((connector) => ["pending", "rollback_pending"].includes(connector.sync_state)) ? (
            <div className="gwHint">Changes take effect once {gateway.name} confirms them.</div>
          ) : null}
        </div>
        {templatesMode === "choose" ? (
          <AddDeviceChooser
            onPickTemplate={() => setTemplatesMode("pick")}
            onNoTemplate={() => { setTemplatesMode(""); setDraft(null); setCreateKind(""); setCreating(true); }}
            onClose={() => setTemplatesMode("")}
          />
        ) : null}
        {templatesMode === "browse" || templatesMode === "pick" ? (
          <DeviceTemplatesModal
            gateway={gateway}
            canConfigure={canConfigure}
            serialPorts={serialPorts}
            onUse={templatesMode === "pick" ? useTemplate : undefined}
            onClose={() => setTemplatesMode("")}
          />
        ) : null}
        {savingTemplate ? (
          <SaveAsTemplateModal
            defaultName={connectorTitle(savingTemplate)}
            buildPayload={(identity) => edgeModbusTemplateFromSpec(edgeModbusSpecFromConnector(savingTemplate), identity)}
            onSaved={(saved) => {
              setSavingTemplate(null);
              notify({
                type: "success",
                title: "Device template saved",
                message: `"${saved?.name || "This device"}" is ready to reuse on the next device of this model.`,
              });
            }}
            onClose={() => setSavingTemplate(null)}
          />
        ) : null}
        {!page ? <div className="gwModalFooter" style={{ justifyContent: "space-between" }}>
          <button type="button" className="gwIconButton" disabled={loading || Boolean(review) || Boolean(historianPrompt)} onClick={() => { setLoading(true); load(); }} aria-label="Refresh tag connections" title="Refresh">
            <img src={refreshIcon} alt="" className="gwIcon" />
          </button>
          <button type="button" className="gwPrimaryBtn" disabled={Boolean(review) || Boolean(historianPrompt)} onClick={onClose}>Done</button>
        </div> : null}
      </div>
    </div>
  );
}

function EdgeConnectionsPage(props) {
  return <EdgeConnectorsModal {...props} page />;
}

export { EdgeConnectionsPage, EdgeConnectorsModal, RevisionReview, SYNC_LABELS, connectorCapabilityLabels, connectorDisplayStatus, connectorForLegacyGPIOUpgrade, connectorIsHumanProtocolService, connectorOperationNeedsRuntimeReadiness, connectorStatusLine, connectorTitle, humanPointAddress, humanizeConnectorDiagnostic, lifecycleReview, preparePendingConnector, retainedGenericUSBConnection, retainedGenericUSBStatusLine, syncPillClass };
